/**
 * Die eine Beschreibung der Navigation.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM EINE DATEI UND NICHT ZWEI
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 13 Stellen im Code beschrieben einmal, wohin ein Klick fuehrt
 * (`docs/frontend-navigation-map.md`, Abschnitt 1). Diese Datei ist die
 * einzige, die Seitenleiste, Brotkrumen und Befehlspalette lesen. Wer hier
 * einen zweiten `STRUKTUR`-aehnlichen Baum in einer Komponente anlegt, hat
 * die Sache verfehlt.
 *
 * Zwei Dinge kommen ausdruecklich NICHT aus dieser Datei:
 *
 * 1. Pfad und Beschriftung der Fachbereiche kommen vom SERVER
 *    (`Fachbereich.pfad` / `.bezeichnung`). Hier steht nur, in welche Gruppe
 *    ein `roleKey` gehoert (`ROLEKEY_GRUPPE`). Ein unbekannter kuenftiger
 *    `roleKey` bleibt sichtbar - er wird ein eigenes Gewerk ohne Unterseiten.
 * 2. Die Gliederung einzelner Gewerke kommt aus einer eigenen
 *    `abschnitte.ts` je Bereich und wird hier EINGELESEN, nicht
 *    abgeschrieben: `seiten/elektro/abschnitte.ts` (`elektroEintraege`),
 *    `seiten/heizlastkuehllast/abschnitte.ts` (`heizlastKuehllastEintraege`,
 *    Paket N1), `seiten/fremdmodelle/abschnitte.ts`
 *    (`fremdmodelleEintraege`, Paket N1 - Sonderfall ohne sichtbare Kinder,
 *    siehe Kopfkommentar dort), `seiten/msr/abschnitte.ts`
 *    (`msrEintraege`, Paket N2), `seiten/aussenanlagen/abschnitte.ts`
 *    (`aussenanlagenEintraege`, Paket N2), `seiten/sprinkler/abschnitte.ts`
 *    (`sprinklerEintraege`, Paket N2) und `seiten/koordination/abschnitte.ts`
 *    (`koordinationEintraege`, Paket N4 - dreizehn von zwanzig internen
 *    Karten, sieben bleiben eingebettet, siehe Kopfkommentar dort), dazu
 *    Heizung, Lüftung (TV5) und `seiten/sanitaer/abschnitte.ts`
 *    (`sanitaerEintraege`, TS9b, Lüftungsmuster mit festen Schlüsseln). Dort
 *    stehen Pfad, Name und Abschnitt; eine zweite Liste waere die sichere
 *    Art, dass eine von beiden vergessen wird. Die Anleitung fuer weitere
 *    Bereiche steht ausfuehrlich im Kopf von
 *    `seiten/heizlastkuehllast/abschnitte.ts` (Paket N1).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER BAUM: GRUPPE → GEWERK → ART → SEITE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis zum 20.09.2026 gruppierte die Leiste nach TAETIGKEIT (Fachplanung,
 * Berechnungen, Dokumentation, Koordination, Standards) und war flach. Ein
 * Gewerk lag dadurch in bis zu fuenf Gruppen: die Luftung stand mit ihrer
 * Anlagenliste in "Fachplanung", mit dem Druckverlust in "Berechnungen",
 * mit den Datenblaettern in "Dokumentation", mit den Bauteilbefunden in
 * "Koordination" und mit den Vorlagen in "Standards".
 *
 * Jetzt gilt: fuenf Gruppen, und die Gruppe GEWERKE ist zweistufig.
 *
 *   `eltern`  Schluessel des uebergeordneten Eintrags. DAS ist die
 *             Zugehoerigkeit - nicht der Pfadanfang. Die zwoelf
 *             Elektro-Unterseiten heissen `elektro-beleuchtung`,
 *             `elektro-verteiler`, ... und sind damit GESCHWISTER von
 *             `elektro`, keine Kinder; `heizung-berechnung` und `geraete`
 *             gehoeren zu Heizung/Kaelte, ohne deren Pfad zu teilen.
 *             Frueher stand dafuer eine Sonderliste `MODUL_ZUGEHOERIGKEIT`
 *             mit dem einen Eintrag `elektro-` daneben; sie ist mit `eltern`
 *             gegenstandslos geworden und ersatzlos entfallen.
 *   `art`     Zwischenzeile innerhalb eines Gewerks. Vier feste Arten
 *             (`planung`, `berechnung`, `daten`, `pruefung`) - oder, bei
 *             Elektro, der Abschnittsname aus `abschnitte.ts`.
 *
 * Der Gewerksknoten SELBST traegt den Pfad der Modulseite; er ist der
 * Verweis auf die Uebersicht des Gewerks und zugleich die Zeile, an der
 * auf- und zugeklappt wird. Deshalb gibt es unter einem Gewerk keine
 * zusaetzliche Zeile "Uebersicht": jede Route steht genau einmal im Baum,
 * und der Vollstaendigkeitstest in `struktur.test.ts` prueft genau das.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS BEWUSST KEIN NAVIGATIONSZIEL IST
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * - Alle `:bauteilId`/`:komponenteId`/`:plantId`/`:gewerk`-Detailrouten: sie
 *   werden aus einer Liste heraus angeklickt, nie aus einem Menue.
 * - `/projekte`: eine Weiterleitung auf das eine Projekt des Containers,
 *   damit alte Lesezeichen nicht ins Leere laufen. Kein Ziel, sondern eine
 *   Bruecke.
 * - Oeffentliche Zweige (`/b/`, `/lk/`, `/s/`, `/ab/`, `/sp/`, `/e/`, `/a/`)
 *   liegen vor der Anmeldeweiche und damit ausserhalb dieses Rahmens.
 * - Die 23 MSR-Unterdateien und die Reiter innerhalb von `/koordination`
 *   steuern keine URL, sondern einen internen Zustandsbaum.
 *
 * `ausschreibung` verweist auf die Weiterleitungsroute `ausschreibung`
 * selbst und nicht auf das fest verdrahtete `ausschreibung/lueftung` - eine
 * Struktur, die ein Gewerk fest waehlt, waere falsch, sobald ein anderes
 * zuerst drankommt.
 */

import type { ComponentType } from 'react';
import type { Fachbereich } from '../api';
import type { IconProps } from '../Icons';
import { ELEKTRO_ABSCHNITTE } from '../seiten/elektro/abschnitte';
import { HEIZLASTKUEHLLAST_ABSCHNITTE } from '../seiten/heizlastkuehllast/abschnitte';
import { FREMDMODELLE_ABSCHNITTE } from '../seiten/fremdmodelle/abschnitte';
import { MSR_ABSCHNITTE } from '../seiten/msr/abschnitte';
import { AUSSENANLAGEN_ABSCHNITTE } from '../seiten/aussenanlagen/abschnitte';
import { SPRINKLER_ABSCHNITTE } from '../seiten/sprinkler/abschnitte';
import { KOORDINATION_ABSCHNITTE } from '../seiten/koordination/abschnitte';
import { HEIZUNG_ABSCHNITTE } from '../seiten/heizung/abschnitte';
import { LUEFTUNG_ABSCHNITTE, type LueftungSinnbild } from '../seiten/lueftung/abschnitte';
import { SANITAER_ABSCHNITTE, type SanitaerSinnbild } from '../seiten/sanitaer/abschnitte';
import {
  IconZustand, IconAuftrag, IconMaschine, IconInstallation, IconModell,
  IconProjekt, IconZeitplan, IconBuch, IconWarnung, IconKennung,
  IconRaum, IconTuer, IconTuertyp, IconLueftung, IconSanitaer, IconHeizung,
  IconSicherheit, IconElektro, IconDatei, IconTabelle, IconSchluessel,
  IconHochladen, IconKi, IconBerechnung, IconKoordination, IconStandard,
  IconVerwaltung, IconNutzung, IconLage, IconFachplanung, IconPdf,
} from '../Icons';

export type Gruppe =
  | 'PROJEKT'
  | 'GEWERKE'
  | 'KOORDINATION'
  | 'UNTERLAGEN'
  | 'VERWALTUNG';

/** Reihenfolge der Gruppen in der Seitenleiste. */
export const GRUPPEN_REIHENFOLGE: readonly Gruppe[] = [
  'PROJEKT', 'GEWERKE', 'KOORDINATION', 'UNTERLAGEN', 'VERWALTUNG',
];

export const GRUPPEN_BESCHRIFTUNG: Record<Gruppe, string> = {
  PROJEKT: 'Projekt',
  GEWERKE: 'Gewerke',
  KOORDINATION: 'Koordination',
  UNTERLAGEN: 'Unterlagen',
  VERWALTUNG: 'Verwaltung',
};

/**
 * Sinnbild je Gruppe. Steht hier und nicht in `Icons.tsx`: Welche Gruppe
 * welches Zeichen traegt, ist eine Aussage ueber die Navigation.
 */
export const GRUPPEN_ICON: Record<Gruppe, ComponentType<IconProps>> = {
  PROJEKT: IconProjekt,
  GEWERKE: IconFachplanung,
  KOORDINATION: IconKoordination,
  UNTERLAGEN: IconBuch,
  VERWALTUNG: IconVerwaltung,
};

/**
 * Die vier Arten, nach denen die Seiten eines Gewerks sortiert und mit einer
 * Zwischenzeile beschriftet werden. Ein `art`-Wert, der hier nicht steht,
 * wird unveraendert als Zwischenzeile gezeigt - so kommen die
 * Abschnittsnamen aus `abschnitte.ts` (Bestand, Netz und Verteilung, ...)
 * ohne Sonderweg durch.
 */
export const ART_BESCHRIFTUNG: Record<string, string> = {
  planung: 'Planung',
  berechnung: 'Berechnungen',
  daten: 'Daten',
  // Begruendete Ausnahme fuer die Koordination (N4, F69, Plan Abschnitt
  // 7/8): sieben Regelseiten unter einer eigenen Zwischenzeile statt in
  // "Daten" untergemischt - eine Ueberschrift, kein anklickbarer Knoten.
  regeln: 'Regeln',
  pruefung: 'Prüfung',
};

/** Beschriftung der Zwischenzeile zu einem `art`-Wert. */
export function artBeschriftung(art: string): string {
  return ART_BESCHRIFTUNG[art] ?? art;
}

/**
 * Wann ein Eintrag sichtbar ist.
 *
 * - `immer`: unabhaengig vom Projektkontext.
 * - `projekt`: nur, wenn ein Projekt offen ist - fester Punkt, kommt nicht
 *   aus dem Fachbereichskatalog.
 * - `fachbereich`: kommt aus `api.projektFachbereiche(id)`; der Eintrag
 *   existiert nur, wenn dieser `roleKey` fuer das Projekt geliefert wird.
 *   Rechte bleiben serverseitig - diese Regel blendet nur aus, was der
 *   Server gar nicht erst mitschickt.
 */
export type Sichtbarkeitsregel =
  | { art: 'immer' }
  | { art: 'projekt' }
  | { art: 'fachbereich'; roleKey: string };

export interface NavigationsEintrag {
  /** Eindeutiger, stabiler Schluessel (fuer React-Keys, Tests, Favoriten). */
  schluessel: string;
  /**
   * Pfad. Bei `sichtbarkeit.art === 'immer'` absolut (`/auftraege`), sonst
   * relativ zu `/projekte/:id/` (`uebersicht`, `lueftung/druckverlust`).
   */
  pfad: string;
  beschriftung: string;
  gruppe: Gruppe;
  Icon: ComponentType<IconProps>;
  sichtbarkeit: Sichtbarkeitsregel;

  /**
   * Schluessel des uebergeordneten Eintrags (Gewerk oder Modulseite). Fehlt
   * er, ist der Eintrag ein Knoten erster Ebene seiner Gruppe.
   *
   * Fehlt der genannte Eltern-Eintrag im aktuellen Katalog (Fachbereich
   * abgeschaltet), entfaellt das Kind mit - `alleEintraege` sortiert es aus.
   * Eine Unterseite ohne ihr Gewerk waere ein Verweis in ein Modul, das
   * dieses Projekt gar nicht fuehrt.
   */
  eltern?: string;

  /** Zwischenzeile innerhalb des Gewerks, siehe `ART_BESCHRIFTUNG`. */
  art?: string;

  /**
   * Reihenfolge der Knoten erster Ebene innerhalb ihrer Gruppe. Fachbereiche
   * bekommen `1000 + reihenfolge` des Servers - ihre Reihenfolge bestimmt
   * damit weiterhin die Datenbank und nicht diese Datei.
   */
  sortierung?: number;

  /**
   * Kleine Marke rechts am Eintrag, z. B. `vorb.` fuer einen Fachbereich,
   * der freigeschaltet, aber noch ohne Aufnahme ist (`istUmgesetzt === false`).
   */
  marke?: string;

  /** Erlaeuterung fuer Tooltip und Befehlspalette. Kommt bei Fachbereichen
   *  vom Server (`Fachbereich.beschreibung`). */
  beschreibung?: string;
}

/**
 * Die feste Navigation: alles, was NICHT aus dem Fachbereichskatalog kommt.
 *
 * Die Reihenfolge innerhalb eines Gewerks folgt der Reihenfolge hier; die
 * Reihenfolge der Gewerke selbst kommt aus `sortierung` (und bei
 * Fachbereichen vom Server).
 */
export const STRUKTUR: readonly NavigationsEintrag[] = [
  // ─── PROJEKT ────────────────────────────────────────────────────────────
  { schluessel: 'uebersicht', pfad: 'uebersicht', beschriftung: 'Übersicht', gruppe: 'PROJEKT', Icon: IconProjekt, sichtbarkeit: { art: 'projekt' }, sortierung: 10 },
  { schluessel: 'planstaende', pfad: 'planstaende', beschriftung: 'Planstände', gruppe: 'PROJEKT', Icon: IconZeitplan, sichtbarkeit: { art: 'projekt' }, sortierung: 20 },
  // TX2 (Befund P9): das Berichtsregister mit den abgelegten Fassungen -
  // gewerkuebergreifend, deshalb unter PROJEKT und nicht unter einem Gewerk.
  { schluessel: 'berichte', pfad: 'berichte', beschriftung: 'Berichte', gruppe: 'PROJEKT', Icon: IconPdf, sichtbarkeit: { art: 'projekt' }, sortierung: 30 },
  // Tuertypen haengt am Tuerbuch (Fachbereich `tueren`) - kein eigener
  // Knoten erster Ebene, sondern dessen Unterseite.
  { schluessel: 'tuertypen', pfad: 'tuertypen', beschriftung: 'Türtypen', gruppe: 'PROJEKT', Icon: IconTuertyp, sichtbarkeit: { art: 'projekt' }, eltern: 'fachbereich-tueren' },
  // Steht hinter Raumbuch und Tuerbuch (Fachbereiche, Sortierung 1001/1002):
  // erst die Grundlagen, dann die Frage, wie weit sie gefuellt sind.
  { schluessel: 'parameter-fuellgrad', pfad: 'parameter-fuellgrad', beschriftung: 'Parameter-Füllgrad', gruppe: 'PROJEKT', Icon: IconTabelle, sichtbarkeit: { art: 'projekt' }, sortierung: 2000 },

  // ─── GEWERKE: Gebaeudehuelle ────────────────────────────────────────────
  { schluessel: 'huelle-bauteilarten', pfad: 'huelle/bauteilarten', beschriftung: 'Bauteilarten', gruppe: 'GEWERKE', Icon: IconRaum, sichtbarkeit: { art: 'projekt' }, eltern: 'fachbereich-huelle', art: 'daten' },

  // ─── GEWERKE: Heizung / Kaelte ──────────────────────────────────────────
  // Seit TH-1 (17.10.2026) EINGELESEN aus `seiten/heizung/abschnitte.ts`
  // ueber `heizungEintraege()` (TX-STANDARD S1.1) - Pfade unveraendert.

  // ─── GEWERKE: Lueftung ──────────────────────────────────────────────────
  // Seit TV5 (18.10.2026) EINGELESEN aus `seiten/lueftung/abschnitte.ts`
  // ueber `lueftungEintraege()` (TX-STANDARD S1.1) - Pfade und Schluessel
  // unveraendert.

  // ─── GEWERKE: Sanitaer ──────────────────────────────────────────────────
  //
  // Seit TS9b (26.10.2026) EINGELESEN aus `seiten/sanitaer/abschnitte.ts`
  // ueber `sanitaerEintraege()` (TX-STANDARD S1.1) - Pfade, Schluessel,
  // Sinnbilder und Zwischenzeilen der bisherigen dreizehn festen Zeilen
  // unveraendert (Lueftungsmuster).

  // ─── GEWERKE: Kueche ────────────────────────────────────────────────────
  //
  // Die Kueche ist kein Fachbereich des Servers; ihre vier Seiten liegen als
  // Unterrouten unter `fremdmodelle` (deshalb die Pfade - keine URL aendert
  // sich). Fachlich ist sie ein Gewerk mit eigener Planung, eigener
  // Aufnahme und eigener Qualitaetspruefung, und sie ist das einzige Modul,
  // in dem die Datenbank fuehrt und das Modell das Ziel ist. Sie steht
  // deshalb als eigenes Gewerk und nicht als Anhaengsel der Fremdmodelle.
  { schluessel: 'kueche', pfad: 'fremdmodelle/kueche', beschriftung: 'Küche', gruppe: 'GEWERKE', Icon: IconNutzung, sichtbarkeit: { art: 'projekt' }, sortierung: 2000 },
  { schluessel: 'kueche-aufnahme', pfad: 'fremdmodelle/kueche/aufnahme', beschriftung: 'Aufnahme', gruppe: 'GEWERKE', Icon: IconHochladen, sichtbarkeit: { art: 'projekt' }, eltern: 'kueche', art: 'daten' },
  { schluessel: 'kueche-zuordnung', pfad: 'fremdmodelle/kueche/zuordnung', beschriftung: 'Zuordnung', gruppe: 'GEWERKE', Icon: IconKennung, sichtbarkeit: { art: 'projekt' }, eltern: 'kueche', art: 'daten' },
  { schluessel: 'kueche-qualitaet', pfad: 'fremdmodelle/kueche/qualitaet', beschriftung: 'Qualität', gruppe: 'GEWERKE', Icon: IconWarnung, sichtbarkeit: { art: 'projekt' }, eltern: 'kueche', art: 'pruefung' },
  // Zwei weitere Reiter derselben Kuechenplanung.tsx als eigene Route (N3,
  // Plan Abschnitt 3.5): "Komponenten" ist bereits der Standardbaum unter
  // `kueche` selbst, "Beobachtungen" bereits `kueche-zuordnung` -
  // "Technikwerte" bekommt bewusst KEINEN Eintrag, es gibt keine Anzeige
  // dafuer (siehe Kopfkommentar der Routenhuellen in Kuechenplanung.tsx).
  { schluessel: 'kueche-anschluesse', pfad: 'fremdmodelle/kueche/anschluesse', beschriftung: 'Anschlüsse', gruppe: 'GEWERKE', Icon: IconTabelle, sichtbarkeit: { art: 'projekt' }, eltern: 'kueche', art: 'daten' },
  { schluessel: 'kueche-plaene', pfad: 'fremdmodelle/kueche/plaene', beschriftung: 'Pläne', gruppe: 'GEWERKE', Icon: IconDatei, sichtbarkeit: { art: 'projekt' }, eltern: 'kueche', art: 'daten' },
  // N5: der erste der zwei Restposten aus N3 ("Raeume" und
  // "Planpositionen", beide von N3 bewusst nicht angefasst). "Raeume"
  // traegt eine eigene Abfrage und eine eigene Schreibaktion (Kopfkommentar
  // `KuecheRaeumeSeite`) - "Planpositionen" bleibt bewusst ohne Eintrag,
  // sie haengt an derselben geteilten Abfrage wie "Komponenten".
  { schluessel: 'kueche-raeume', pfad: 'fremdmodelle/kueche/raeume', beschriftung: 'Räume', gruppe: 'GEWERKE', Icon: IconRaum, sichtbarkeit: { art: 'projekt' }, eltern: 'kueche', art: 'daten' },

  // ─── KOORDINATION: Aussparungen (Fachbereich) ──────────────────────────
  //
  // Der Wurzelknoten `aussparungen` selbst kommt vom Server (roleKey
  // `aussparungen`, siehe ROLEKEY_GRUPPE) - hier stehen nur seine drei
  // Unterseiten. Alle drei waren bis N3 ausschliesslich ueber den internen
  // Reiter von Aussparungen.tsx erreichbar (Plan Abschnitt 3.6); sie haben
  // jetzt eine eigene Route (App.tsx, `AussparungSeiten.tsx`), der Reiter
  // selbst bleibt zusaetzlich bestehen.
  { schluessel: 'aussparungen-abgleich', pfad: 'aussparungen/abgleich', beschriftung: 'Abgleich', gruppe: 'KOORDINATION', Icon: IconModell, sichtbarkeit: { art: 'projekt' }, eltern: 'fachbereich-aussparungen', art: 'berechnung' },
  { schluessel: 'aussparungen-arbeit', pfad: 'aussparungen/arbeit', beschriftung: 'Arbeitsvorrat', gruppe: 'KOORDINATION', Icon: IconTabelle, sichtbarkeit: { art: 'projekt' }, eltern: 'fachbereich-aussparungen', art: 'pruefung' },
  { schluessel: 'aussparungen-baum', pfad: 'aussparungen/baum', beschriftung: 'Baum', gruppe: 'KOORDINATION', Icon: IconRaum, sichtbarkeit: { art: 'projekt' }, eltern: 'fachbereich-aussparungen', art: 'pruefung' },

  // ─── KOORDINATION ───────────────────────────────────────────────────────
  //
  // Die fuenf Unterseiten waren bisher nur aus dem Standardreiter der
  // Koordinationsseite heraus erreichbar und standen in keinem Menue.
  { schluessel: 'koordination', pfad: 'koordination', beschriftung: 'Koordination', gruppe: 'KOORDINATION', Icon: IconKoordination, sichtbarkeit: { art: 'projekt' }, sortierung: 10 },
  { schluessel: 'koordination-belegung', pfad: 'koordination/belegung', beschriftung: 'Belegung', gruppe: 'KOORDINATION', Icon: IconLage, sichtbarkeit: { art: 'projekt' }, eltern: 'koordination' },
  { schluessel: 'koordination-reservierungen', pfad: 'koordination/reservierungen', beschriftung: 'Reservierungen', gruppe: 'KOORDINATION', Icon: IconSchluessel, sichtbarkeit: { art: 'projekt' }, eltern: 'koordination' },
  { schluessel: 'koordination-nachtlauf', pfad: 'koordination/nachtlauf', beschriftung: 'Nachtlauf', gruppe: 'KOORDINATION', Icon: IconZeitplan, sichtbarkeit: { art: 'projekt' }, eltern: 'koordination' },
  { schluessel: 'koordination-sammel', pfad: 'koordination/sammel', beschriftung: 'Sammelliste', gruppe: 'KOORDINATION', Icon: IconTabelle, sichtbarkeit: { art: 'projekt' }, eltern: 'koordination' },
  { schluessel: 'koordination-projektleitung', pfad: 'koordination/projektleitung', beschriftung: 'Projektleitung', gruppe: 'KOORDINATION', Icon: IconProjekt, sichtbarkeit: { art: 'projekt' }, eltern: 'koordination' },

  // ─── UNTERLAGEN ─────────────────────────────────────────────────────────
  { schluessel: 'ausschreibung', pfad: 'ausschreibung', beschriftung: 'Ausschreibung', gruppe: 'UNTERLAGEN', Icon: IconBuch, sichtbarkeit: { art: 'projekt' }, sortierung: 10 },
  // Die funktionale Leistungsbeschreibung (Plan Abschnitt 3.9): kein eigener
  // Fachbereich, sondern ein Unterpunkt der Ausschreibung - sie teilt Titel,
  // Kostengruppen und Laeufe mit dem Standard-LV und unterscheidet sich nur
  // im Text. Bis N3 stand sie als eigener Wurzelknoten neben `ausschreibung`
  // in derselben Gruppe; jetzt haengt sie darunter (`eltern`), ihr Pfad
  // `funktionalbeschreibung` bleibt unveraendert.
  { schluessel: 'funktionalbeschreibung', pfad: 'funktionalbeschreibung', beschriftung: 'Funktionale Beschreibung', gruppe: 'UNTERLAGEN', Icon: IconTabelle, sichtbarkeit: { art: 'projekt' }, eltern: 'ausschreibung', art: 'daten' },
  // Gesamt-LV (Plan Abschnitt 3.8, "berechnung"): projektweit, kein
  // Gewerk-Bezug - anders als Grundlagen (Mengen-/Merkmalregeln), die am
  // `:gewerk`-Parameter haengen und deshalb KEINEN eigenen Sidebar-Eintrag
  // bekommen (dieselbe Mehrdeutigkeit wie bei `ausschreibung` selbst, siehe
  // Kopfkommentar dieser Datei, Zeile 77-80, und AusschreibungSeiten.tsx).
  { schluessel: 'ausschreibung-gesamt', pfad: 'ausschreibung/gesamt', beschriftung: 'Gesamt-LV', gruppe: 'UNTERLAGEN', Icon: IconBerechnung, sichtbarkeit: { art: 'projekt' }, eltern: 'ausschreibung', art: 'berechnung' },
  { schluessel: 'ai-completion', pfad: 'ai-completion', beschriftung: 'AI Completion', gruppe: 'UNTERLAGEN', Icon: IconKi, sichtbarkeit: { art: 'projekt' }, sortierung: 30 },

  // ─── VERWALTUNG ─────────────────────────────────────────────────────────
  { schluessel: 'normwerte', pfad: 'normwerte', beschriftung: 'Normwerte', gruppe: 'VERWALTUNG', Icon: IconStandard, sichtbarkeit: { art: 'projekt' }, sortierung: 10 },
  { schluessel: 'modellvorgaben', pfad: '/modellvorgaben', beschriftung: 'Modellvorgaben', gruppe: 'VERWALTUNG', Icon: IconModell, sichtbarkeit: { art: 'immer' }, sortierung: 20 },
  { schluessel: 'profile', pfad: 'profile', beschriftung: 'Exportprofile', gruppe: 'VERWALTUNG', Icon: IconSchluessel, sichtbarkeit: { art: 'projekt' }, sortierung: 30 },
  { schluessel: 'einstellungen', pfad: 'einstellungen', beschriftung: 'Einstellungen', gruppe: 'VERWALTUNG', Icon: IconVerwaltung, sichtbarkeit: { art: 'projekt' }, sortierung: 40 },
  // AC1 (F39 „ACC zuerst“): Verbindung zu Autodesk, Zuordnung der ACC-Modelle, neue Versionen.
  { schluessel: 'acc', pfad: 'acc', beschriftung: 'ACC verbinden', gruppe: 'VERWALTUNG', Icon: IconModell, sichtbarkeit: { art: 'projekt' }, sortierung: 45 },
  { schluessel: 'zustand', pfad: '/', beschriftung: 'Zustand', gruppe: 'VERWALTUNG', Icon: IconZustand, sichtbarkeit: { art: 'immer' }, sortierung: 50 },
  { schluessel: 'auftraege', pfad: '/auftraege', beschriftung: 'Aufträge', gruppe: 'VERWALTUNG', Icon: IconAuftrag, sichtbarkeit: { art: 'immer' }, sortierung: 60 },
  { schluessel: 'maschinen', pfad: '/maschinen', beschriftung: 'Maschinen', gruppe: 'VERWALTUNG', Icon: IconMaschine, sichtbarkeit: { art: 'immer' }, sortierung: 70 },
  { schluessel: 'installation', pfad: '/installation', beschriftung: 'Installation', gruppe: 'VERWALTUNG', Icon: IconInstallation, sichtbarkeit: { art: 'immer' }, sortierung: 80 },
];

/**
 * BIM12 (F147/F148): „BIM-Modelle“ - das nur lesende Modellregister des BIM-Kerns (eigene TGA-Modelle und
 * Fremdmodelle). Steht NICHT in `STRUKTUR`, weil es nur erscheint, wenn der Server den BIM-Kern freigeschaltet hat
 * (`Projekt.bimModelle`, Schalter `Bim:Schatten`): ohne Schalter ist das Release für die Navigation wirkungslos.
 * `alleEintraege` nimmt ihn mit `{ bimModelle: true }` auf; unter PROJEKT direkt nach den Planständen.
 */
export const BIM_MODELLE_EINTRAG: NavigationsEintrag = { schluessel: 'bim-modelle', pfad: 'bim-modelle', beschriftung: 'BIM-Modelle', gruppe: 'PROJEKT', Icon: IconModell, sichtbarkeit: { art: 'projekt' }, sortierung: 25, beschreibung: 'Alle Modelle der BIM-Aufnahme: eigene TGA-Modelle und verknüpfte Fremdmodelle (nur lesend)' };

/** Zusätzliche, vom Server freigeschaltete Einträge für `alleEintraege`. */
export interface Eintragsoptionen {
  /** BIM12: der Server hat den BIM-Kern freigeschaltet (`Projekt.bimModelle`). */
  bimModelle?: boolean;
}

/**
 * Die Optionen aus dem Projekt der Projektliste (`/api/projects`). Nur ein echtes `true` schaltet frei - ein älterer
 * Server ohne das Feld oder ein fehlendes Projekt lassen den Eintrag weg.
 */
export function eintragsoptionen(projekt: { bimModelle?: boolean } | null | undefined): Eintragsoptionen {
  return { bimModelle: projekt?.bimModelle === true };
}

/**
 * Gruppenzuordnung der servergelieferten Fachbereiche. Schluessel ist
 * `Fachbereich.roleKey`.
 *
 * Alles, was hier NICHT steht, wird ein Gewerk (siehe Rueckfall unten) -
 * ein kuenftiger `roleKey` verschwindet damit nicht stillschweigend.
 */
export const ROLEKEY_GRUPPE: Record<string, Gruppe> = {
  raumbuch: 'PROJEKT',
  tueren: 'PROJEKT',
  aussparungen: 'KOORDINATION',
  // `fremdmodelle` kam bis zum 20.09.2026 DOPPELT vor: als fester Eintrag in
  // dieser Datei und als Fachbereich vom Server. Der feste ist entfallen;
  // Pfad und Beschriftung kommen jetzt wie bei jedem anderen Fachbereich
  // aus einer Quelle.
  fremdmodelle: 'KOORDINATION',
  huelle: 'GEWERKE',
  aussenanlagen: 'GEWERKE',
  heizlast_kuehllast: 'GEWERKE',
  lueftung: 'GEWERKE',
  sanitaer: 'GEWERKE',
  heizung_kaelte: 'GEWERKE',
  sprinkler: 'GEWERKE',
  elektro: 'GEWERKE',
  msr: 'GEWERKE',
};

/** Gruppe fuer einen unbekannten, kuenftigen roleKey: ein eigenes Gewerk
 *  ohne Unterseiten - sichtbar bleiben statt stillschweigend verschwinden. */
const ROLEKEY_GRUPPE_FALLBACK: Gruppe = 'GEWERKE';

/** Sinnbild je Fachbereich. */
export const ROLEKEY_ICON: Record<string, ComponentType<IconProps>> = {
  raumbuch: IconRaum,
  tueren: IconTuer,
  lueftung: IconLueftung,
  sanitaer: IconSanitaer,
  heizung_kaelte: IconHeizung,
  sprinkler: IconSicherheit,
  elektro: IconElektro,
  msr: IconKennung,
  huelle: IconRaum,
  // TS9a: ein eigenes Sinnbild (Lage) statt des Sanitär-Symbols (TS-Befund §6.3).
  aussenanlagen: IconLage,
  aussparungen: IconModell,
  heizlast_kuehllast: IconHeizung,
  fremdmodelle: IconDatei,
};

const ROLEKEY_ICON_FALLBACK: ComponentType<IconProps> = IconModell;

/** Schluessel des Navigationseintrags eines Fachbereichs. */
export function fachbereichSchluessel(roleKey: string): string {
  return `fachbereich-${roleKey}`;
}

/**
 * Baut aus dem servergelieferten Fachbereichskatalog eines Projekts die
 * dazugehoerigen Navigationseintraege. Pfad und Beschriftung kommen
 * unveraendert vom Server - es entsteht keine zweite Quelle dafuer.
 */
export function eintraegeAusFachbereichen(
  bereiche: readonly Fachbereich[],
): NavigationsEintrag[] {
  return bereiche.map((bereich) => ({
    schluessel: fachbereichSchluessel(bereich.roleKey),
    pfad: bereich.pfad,
    beschriftung: bereich.bezeichnung,
    gruppe: ROLEKEY_GRUPPE[bereich.roleKey] ?? ROLEKEY_GRUPPE_FALLBACK,
    Icon: ROLEKEY_ICON[bereich.roleKey] ?? ROLEKEY_ICON_FALLBACK,
    sichtbarkeit: { art: 'fachbereich', roleKey: bereich.roleKey },
    // Die Reihenfolge der Gewerke bestimmt der Server, nicht diese Datei.
    sortierung: 1000 + bereich.reihenfolge,
    marke: bereich.istUmgesetzt ? undefined : 'vorb.',
    beschreibung: bereich.beschreibung ?? undefined,
  }));
}

/**
 * Die Unterseiten des Gewerks Elektro - EINGELESEN aus
 * `seiten/elektro/abschnitte.ts`, nicht abgeschrieben. Abschnittsname wird
 * zur Zwischenzeile (`art`), Pfad und Name kommen von dort.
 *
 * Der Eintrag, der auf die Modulseite selbst zeigt (`elektro`), faellt in
 * `alleEintraege` heraus: dorthin fuehrt bereits der Gewerksknoten.
 */
export function elektroEintraege(): NavigationsEintrag[] {
  return ELEKTRO_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    // Der Pfad ist zugleich der Schluessel - er ist eindeutig und lesbar.
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: IconElektro,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('elektro'),
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/**
 * Die Unterseiten des Gewerks Heizung/Kälte - EINGELESEN aus
 * `seiten/heizung/abschnitte.ts` (TH-1, TX-STANDARD S1.1). Selbes Muster
 * wie `elektroEintraege`: Abschnittsname wird zur Zwischenzeile (`art`), der
 * Eintrag mit `pfad === 'heizung-kaelte'` (die Übersicht) faellt in
 * `alleEintraege` automatisch heraus, weil er dem Pfad seines
 * Elternknotens entspricht.
 */
export function heizungEintraege(): NavigationsEintrag[] {
  return HEIZUNG_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: IconHeizung,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('heizung_kaelte'),
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/** Die Sinnbilder der Lüftungseinträge - wie vor dem Umzug je Eintrag (TV5). */
const LUEFTUNG_SINNBILD: Record<LueftungSinnbild, ComponentType<IconProps>> = {
  lueftung: IconLueftung,
  berechnung: IconBerechnung,
  tabelle: IconTabelle,
  datei: IconDatei,
  hochladen: IconHochladen,
  standard: IconStandard,
  warnung: IconWarnung,
};

/**
 * Die Unterseiten des Gewerks Lüftung - EINGELESEN aus
 * `seiten/lueftung/abschnitte.ts` (TV5, TX-STANDARD S1.1). Anders als bei
 * Heizung bleiben Schluessel und Zwischenzeile (`art`) die der zehn
 * bisherigen Einzeleintraege; nur die vier Pruefungsseiten aus TV5 tragen eine
 * Erlaeuterung.
 */
export function lueftungEintraege(): NavigationsEintrag[] {
  return LUEFTUNG_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.schluessel,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: LUEFTUNG_SINNBILD[e.sinnbild],
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('lueftung'),
    art: abschnitt.art,
    ...(e.hinweis ? { beschreibung: e.hinweis } : {}),
  })));
}

const SANITAER_SINNBILD: Record<SanitaerSinnbild, ComponentType<IconProps>> = {
  sanitaer: IconSanitaer,
  berechnung: IconBerechnung,
  tabelle: IconTabelle,
  raum: IconRaum,
  datei: IconDatei,
  warnung: IconWarnung,
};

/**
 * Die Unterseiten des Gewerks Sanitär - EINGELESEN aus
 * `seiten/sanitaer/abschnitte.ts` (TS9b, TX-STANDARD S1.1) im Muster der
 * Lüftung: Schlüssel, Sinnbild und Zwischenzeile (`art`) der bisherigen
 * dreizehn festen Zeilen bleiben; neu ist die Erläuterung je Seite.
 */
export function sanitaerEintraege(): NavigationsEintrag[] {
  return SANITAER_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.schluessel,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: SANITAER_SINNBILD[e.sinnbild],
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('sanitaer'),
    art: abschnitt.art,
    beschreibung: e.hinweis,
  })));
}

/**
 * Die Unterseiten des Gewerks Heiz-/Kühllast - EINGELESEN aus
 * `seiten/heizlastkuehllast/abschnitte.ts` (Paket N1). Selbes Muster wie
 * `elektroEintraege`: Abschnittsname wird zur Zwischenzeile (`art`), der
 * Eintrag mit `pfad === 'heizlast-kuehllast'` faellt in `alleEintraege`
 * automatisch heraus, weil er dem Pfad seines Elternknotens entspricht.
 */
export function heizlastKuehllastEintraege(): NavigationsEintrag[] {
  return HEIZLASTKUEHLLAST_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: IconHeizung,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('heizlast_kuehllast'),
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/**
 * Die Unterseiten des Gewerks MSR/Gebäudeautomation - EINGELESEN aus
 * `seiten/msr/abschnitte.ts` (Paket N2). Selbes Muster wie
 * `heizlastKuehllastEintraege`: Abschnittsname wird zur Zwischenzeile
 * (`art`), der Eintrag mit `pfad === 'msr'` (die Übersicht) faellt in
 * `alleEintraege` automatisch heraus.
 */
export function msrEintraege(): NavigationsEintrag[] {
  return MSR_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: IconKennung,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('msr'),
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/**
 * Die Unterseiten des Gewerks Außenanlagen - EINGELESEN aus
 * `seiten/aussenanlagen/abschnitte.ts` (Paket N2). Selbes Muster wie
 * `heizlastKuehllastEintraege`: Abschnittsname wird zur Zwischenzeile
 * (`art`), der Eintrag mit `pfad === 'aussenanlagen'` (Bestand) faellt in
 * `alleEintraege` automatisch heraus.
 */
export function aussenanlagenEintraege(): NavigationsEintrag[] {
  return AUSSENANLAGEN_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: IconLage,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('aussenanlagen'),
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/**
 * Die Unterseiten des Gewerks Sprinkler - EINGELESEN aus
 * `seiten/sprinkler/abschnitte.ts` (Paket N2). Selbes Muster wie
 * `heizlastKuehllastEintraege`: Abschnittsname wird zur Zwischenzeile
 * (`art`), der Eintrag mit `pfad === 'sprinkler'` (Bestand) faellt in
 * `alleEintraege` automatisch heraus.
 */
export function sprinklerEintraege(): NavigationsEintrag[] {
  return SPRINKLER_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'GEWERKE' as Gruppe,
    Icon: IconSicherheit,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('sprinkler'),
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/**
 * Die Unterseiten des Gewerks Fremdmodelle - EINGELESEN aus
 * `seiten/fremdmodelle/abschnitte.ts` (Paket N1, Sonderfall: der einzige
 * Eintrag dort zeigt auf die Modulseite selbst und faellt deshalb in
 * `alleEintraege` ebenfalls heraus - siehe Kopfkommentar der Quelldatei).
 */
export function fremdmodelleEintraege(): NavigationsEintrag[] {
  return FREMDMODELLE_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'KOORDINATION' as Gruppe,
    Icon: IconDatei,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: fachbereichSchluessel('fremdmodelle'),
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/**
 * Die Unterseiten der Koordination - EINGELESEN aus
 * `seiten/koordination/abschnitte.ts` (N4, F69). Anders als bei den
 * Gewerken oben ist `koordination` kein Fachbereich vom Server, sondern der
 * feste STRUKTUR-Eintrag mit Schluessel `koordination` - deshalb `eltern:
 * 'koordination'` statt `fachbereichSchluessel(...)`. Selbes Muster wie
 * `elektroEintraege`: Abschnittsname wird zur Zwischenzeile (`art`).
 */
export function koordinationEintraege(): NavigationsEintrag[] {
  return KOORDINATION_ABSCHNITTE.flatMap((abschnitt) => abschnitt.eintraege.map((e) => ({
    schluessel: e.pfad,
    pfad: e.pfad,
    beschriftung: e.name,
    gruppe: 'KOORDINATION' as Gruppe,
    Icon: IconKoordination,
    sichtbarkeit: { art: 'projekt' } as Sichtbarkeitsregel,
    eltern: 'koordination',
    art: abschnitt.name,
    beschreibung: e.hinweis,
  })));
}

/** Absoluter Pfad eines Eintrags, je nach Sichtbarkeitsregel. */
export function absoluterPfad(eintrag: NavigationsEintrag, projektId?: string): string {
  if (eintrag.sichtbarkeit.art === 'immer') return eintrag.pfad;
  if (!projektId) return eintrag.pfad;
  return `/projekte/${projektId}/${eintrag.pfad}`;
}

/**
 * Die vollstaendige Eintragsliste fuer Seitenleiste, Brotkrumen und
 * Befehlspalette - an EINER Stelle zusammengesetzt, damit die drei nicht
 * auseinanderlaufen koennen.
 *
 * Ohne offenes Projekt bleiben die projektgebundenen Eintraege weg: ihr
 * Pfad waere ohne Projektkennung nicht bildbar, und ein Verweis ins Leere
 * ist schlimmer als ein fehlender.
 *
 * Zwei Filter am Ende, beide aus derselben Ueberlegung:
 * - Ein Kind ohne seinen Eltern-Eintrag entfaellt (abgeschaltetes Gewerk).
 * - Ein Kind, dessen Pfad der seines Elternteils IST, entfaellt: dorthin
 *   fuehrt der Gewerksknoten schon. Das trifft `elektro` aus
 *   `abschnitte.ts` - und wuerde jeden kuenftigen gleichartigen Fall
 *   genauso treffen, ohne Sonderregel fuer Elektro.
 */
export function alleEintraege(
  bereiche: readonly Fachbereich[],
  projektId: string | undefined,
  optionen: Eintragsoptionen = {},
): NavigationsEintrag[] {
  const fest = STRUKTUR.filter(
    (e) => e.sichtbarkeit.art === 'immer' || projektId !== undefined,
  );
  // BIM12: nur mit Projekt UND freigeschaltetem BIM-Kern.
  if (projektId !== undefined && optionen.bimModelle === true) fest.push(BIM_MODELLE_EINTRAG);
  const fach = projektId ? eintraegeAusFachbereichen(bereiche) : [];
  const elektro = projektId ? elektroEintraege() : [];
  const heizlastKuehllast = projektId ? heizlastKuehllastEintraege() : [];
  const fremdmodelle = projektId ? fremdmodelleEintraege() : [];
  const msr = projektId ? msrEintraege() : [];
  const aussenanlagen = projektId ? aussenanlagenEintraege() : [];
  const sprinkler = projektId ? sprinklerEintraege() : [];
  const koordination = projektId ? koordinationEintraege() : [];
  const heizung = projektId ? heizungEintraege() : [];
  const lueftung = projektId ? lueftungEintraege() : [];
  const sanitaer = projektId ? sanitaerEintraege() : [];

  const roh = [
    ...fest, ...fach, ...elektro, ...heizlastKuehllast, ...fremdmodelle, ...msr,
    ...aussenanlagen, ...sprinkler, ...koordination, ...heizung, ...lueftung, ...sanitaer,
  ];
  const nachSchluessel = new Map(roh.map((e) => [e.schluessel, e]));

  return roh.filter((e) => {
    if (!e.eltern) return true;
    const eltern = nachSchluessel.get(e.eltern);
    if (!eltern) return false;
    return eltern.pfad !== e.pfad;
  });
}

/**
 * Ob ein Eintrag zum aktuellen Pfad genau passt.
 *
 * Nur GENAU - die Zugehoerigkeit eines Elternknotens beantwortet `eltern`
 * und nicht der Pfadanfang. Sonst waere `fremdmodelle` auf
 * `fremdmodelle/kueche/aufnahme` markiert, obwohl die Kueche ein eigenes
 * Gewerk ist, und `elektro` auf `elektro-beleuchtung` NICHT markiert,
 * obwohl es dasselbe Gewerk ist.
 */
export function istAktiv(eintragPfadAbsolut: string, aktuellerPfad: string): boolean {
  return eintragPfadAbsolut === aktuellerPfad;
}

/**
 * Der Eintrag, auf dem man steht.
 *
 * Erst der genaue Treffer. Gibt es keinen, gilt der Eintrag mit dem
 * laengsten Pfad, unter dem der aktuelle Pfad liegt - das faengt die
 * Detailrouten ab, die selbst kein Navigationsziel sind
 * (`geraete/:bauteilId` markiert die Geraeteliste, `auftraege/:id` die
 * Auftragsliste). Ohne diesen zweiten Schritt stuende man auf einer
 * Detailseite in einer Leiste, die nichts markiert.
 */
export function aktiverEintrag(
  eintraege: readonly NavigationsEintrag[],
  projektId: string | undefined,
  aktuellerPfad: string,
): NavigationsEintrag | null {
  const genau = eintraege.find(
    (e) => absoluterPfad(e, projektId) === aktuellerPfad,
  );
  if (genau) return genau;

  let treffer: NavigationsEintrag | null = null;
  let laenge = -1;
  for (const e of eintraege) {
    const abs = absoluterPfad(e, projektId);
    if (abs === '/') continue; // Die Wurzel liegt unter allem, markiert aber nichts.
    if (aktuellerPfad.startsWith(`${abs}/`) && abs.length > laenge) {
      treffer = e;
      laenge = abs.length;
    }
  }
  return treffer;
}

/**
 * Die Kette vom Knoten erster Ebene bis zum aktuellen Eintrag, ueber
 * `eltern`. Das ist zugleich die Brotkrume und die Antwort auf die Frage,
 * welches Gewerk offen sein muss.
 */
export function aktiveEintraege(
  eintraege: readonly NavigationsEintrag[],
  projektId: string | undefined,
  aktuellerPfad: string,
): NavigationsEintrag[] {
  const nachSchluessel = new Map(eintraege.map((e) => [e.schluessel, e]));
  const kette: NavigationsEintrag[] = [];

  let lauf = aktiverEintrag(eintraege, projektId, aktuellerPfad);
  const gesehen = new Set<string>();
  while (lauf && !gesehen.has(lauf.schluessel)) {
    gesehen.add(lauf.schluessel);
    kette.unshift(lauf);
    lauf = lauf.eltern ? nachSchluessel.get(lauf.eltern) ?? null : null;
  }
  return kette;
}

/** Brotkrumen-Kette: Knoten erster Ebene zuerst, aktueller Eintrag zuletzt. */
export function breadcrumbPfad(
  eintraege: readonly NavigationsEintrag[],
  projektId: string | undefined,
  aktuellerPfad: string,
): NavigationsEintrag[] {
  return aktiveEintraege(eintraege, projektId, aktuellerPfad);
}

/** Die Schluessel aller Eintraege der aktiven Kette - fuer die Leiste. */
export function aktiveSchluessel(
  eintraege: readonly NavigationsEintrag[],
  projektId: string | undefined,
  aktuellerPfad: string,
): Set<string> {
  return new Set(
    aktiveEintraege(eintraege, projektId, aktuellerPfad).map((e) => e.schluessel),
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DER BAUM
   ═══════════════════════════════════════════════════════════════════════════ */

/** Eine Zwischenzeile innerhalb eines Gewerks mit ihren Seiten. */
export interface Artgruppe {
  /** Schluessel der Art (`daten`) oder Abschnittsname aus `abschnitte.ts`. */
  art: string;
  /** Was in der Zwischenzeile steht; leer, wenn ohne Zwischenzeile. */
  beschriftung: string;
  eintraege: NavigationsEintrag[];
}

/** Ein Knoten erster Ebene (Gewerk oder Modulseite) mit seinen Unterseiten. */
export interface Baumknoten {
  eintrag: NavigationsEintrag;
  arten: Artgruppe[];
}

export interface Baumgruppe {
  gruppe: Gruppe;
  knoten: Baumknoten[];
}

/**
 * Gruppe → Knoten → Art → Seite. Die einzige Stelle, an der der Baum
 * entsteht; Seitenleiste, Brotkrumen und Befehlspalette lesen ihn.
 *
 * Kinder behalten die Reihenfolge der Eingabeliste, Arten die Reihenfolge
 * ihres ersten Auftretens - so steht bei Elektro die Gliederung aus
 * `abschnitte.ts` unveraendert da, ohne dass hier eine zweite
 * Reihenfolgetabelle gepflegt werden muesste.
 */
export function baum(eintraege: readonly NavigationsEintrag[]): Baumgruppe[] {
  const vorhanden = new Set(eintraege.map((e) => e.schluessel));
  const kinderVon = new Map<string, NavigationsEintrag[]>();
  const wurzeln: NavigationsEintrag[] = [];

  for (const e of eintraege) {
    if (e.eltern && vorhanden.has(e.eltern)) {
      const liste = kinderVon.get(e.eltern) ?? [];
      liste.push(e);
      kinderVon.set(e.eltern, liste);
    } else {
      // Auch ein Kind mit unbekanntem Elternteil bleibt sichtbar - lieber an
      // der falschen Stelle als gar nicht.
      wurzeln.push(e);
    }
  }

  return GRUPPEN_REIHENFOLGE
    .map((gruppe) => ({
      gruppe,
      knoten: wurzeln
        .filter((e) => e.gruppe === gruppe)
        .slice()
        .sort((a, b) => (a.sortierung ?? 500) - (b.sortierung ?? 500))
        .map((eintrag) => ({ eintrag, arten: artenVon(kinderVon.get(eintrag.schluessel) ?? []) })),
    }))
    .filter((g) => g.knoten.length > 0);
}

function artenVon(kinder: readonly NavigationsEintrag[]): Artgruppe[] {
  const arten: Artgruppe[] = [];
  for (const kind of kinder) {
    const art = kind.art ?? '';
    let gruppe = arten.find((a) => a.art === art);
    if (!gruppe) {
      gruppe = { art, beschriftung: art === '' ? '' : artBeschriftung(art), eintraege: [] };
      arten.push(gruppe);
    }
    gruppe.eintraege.push(kind);
  }
  return arten;
}

/**
 * Das Gewerk (der Knoten erster Ebene), zu dem ein Eintrag gehoert - fuer
 * die Zusatzzeile der Befehlspalette ("Druckverlust — Lüftung").
 */
export function elternKnoten(
  eintraege: readonly NavigationsEintrag[],
  eintrag: NavigationsEintrag,
): NavigationsEintrag | null {
  const nachSchluessel = new Map(eintraege.map((e) => [e.schluessel, e]));
  const gesehen = new Set<string>([eintrag.schluessel]);
  let lauf: NavigationsEintrag | null = null;
  let naechsterSchluessel = eintrag.eltern;

  while (naechsterSchluessel && !gesehen.has(naechsterSchluessel)) {
    gesehen.add(naechsterSchluessel);
    const gefunden = nachSchluessel.get(naechsterSchluessel);
    if (!gefunden) break;
    lauf = gefunden;
    naechsterSchluessel = gefunden.eltern;
  }
  return lauf;
}

/**
 * Sucht in Beschriftung, Gruppe und Beschreibung - fuer die Befehlspalette.
 * Buchstabenfolge, nicht Wortanfang: "druck" findet "Druckverlust", "verl"
 * ebenso. Ohne Sucheingabe passt alles.
 */
export function passtZuSuche(eintrag: NavigationsEintrag, suche: string): boolean {
  const begriff = suche.trim().toLowerCase();
  if (begriff === '') return true;
  const heuhaufen = [
    eintrag.beschriftung,
    GRUPPEN_BESCHRIFTUNG[eintrag.gruppe],
    eintrag.art ? artBeschriftung(eintrag.art) : '',
    eintrag.beschreibung ?? '',
    eintrag.pfad,
  ].join(' ').toLowerCase();
  return begriff.split(/\s+/).every((teil) => heuhaufen.includes(teil));
}
